import React, { useCallback, useEffect, useState, useRef } from "react";
import { checkoutStyles } from "./CheckoutStyles";
import { Button, SwitchOnChangeData, mergeClasses } from "@fluentui/react-components";
import { ConfirmationDialog, ConfirmationDialogProps } from "../shared";
import { Payments } from "./Payments";
import { useNavigate, useParams } from "react-router-dom";
import {
  CardFormValues,
  PaymentType,
  appInsightsEvents,
  trackEvent,
  useAppStore,
} from "../../common";
import { CartHeader } from "./CartHeader";
import { CartEntries } from "./CartEntries";
import { CartStatus, OrderStatus } from "@dining/api-client";
import { Form, FormikProvider, useFormik } from "formik";
import { LoadingScreen } from "../shared";

const useStyles = checkoutStyles;

export const Checkout = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { cafeId } = useParams();
  const [dialogProps, setDialogProps] = useState<ConfirmationDialogProps>();

  const [
    appInsights,
    applePayEnabled,
    cart,
    context,
    orders,
    payWithApplePay,
    paymentConfig,
    fetchPaymentConfig,
  ] = useAppStore((state) => [
    state.appInsights,
    state.applePayEnabled,
    state.cart,
    state.context,
    state.orders,
    state.payWithApplePay,
    state.paymentConfig,
    state.fetchPaymentConfig,
  ]);
  const {
    selected: currentOrder,
    setSelected: setSelectedOrder,
    submit,
    loading: loadingOrder,
    error: orderError,
    setError: setOrderError,
  } = orders;
  const {
    value: currentCart,
    loading: loadingCart,
    deleteItemFromCart,
    getCart,
    checkoutCart,
    abandonCart,
    setCart,
  } = cart;
  const [initialPaymentType, setInitialPaymentType] = useState<PaymentType>("Card");
  const lastRemoveButtonRef = useRef<React.RefObject<HTMLButtonElement> | null>(null);
  useEffect(() => {
    trackEvent(appInsights, appInsightsEvents.Usage.ViewComponent, context, {
      componentName: "Checkout",
    });
    document.title = "Check Out - Microsoft Dining";

    return () => {
      setOrderError(undefined);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setOrderError is a stable Zustand action
  }, []);

  useEffect(() => {
    if (!paymentConfig) {
      fetchPaymentConfig();
    }
  }, [fetchPaymentConfig, paymentConfig]);

  useEffect(() => {
    if (!currentCart && cafeId) {
      getCart();
    }
    if (currentCart && currentCart.status !== CartStatus.CheckedOut && cafeId) {
      checkoutCart(cafeId);

      // Reset the selected order when checking out. This is done specifically to prevent
      // the user from navigating away to the detail page for the previous order when trying to
      // check out the cart with exact same items as previous order. Note that selected order
      // will get reset to correct value when order is placed - so accurate state will be restored.
      setSelectedOrder(undefined);
    }
  }, [currentCart, cafeId]);

  useEffect(() => {
    if (
      currentOrder &&
      (currentOrder.status === OrderStatus.SubmittedToVendor ||
        currentOrder.status === OrderStatus.ReadyForPickup) &&
      currentCart?.status === CartStatus.CheckedOut
    ) {
      setCart(undefined);
      // Wipe the navigation history for successful order.
      window.history.pushState({}, "", "/buildings");

      navigate(`/cafe/${cafeId}/order/${currentOrder.id}`);
    }
  }, [currentOrder, currentCart, navigate, cafeId, appInsights, context]);

  useEffect(() => {
    orderError &&
      setDialogProps({
        title: "Something went wrong",
        content:
          "Payment information was rejected. Please update your payment information and try again.",
        open: true,
        showConfirmAction: false,
        dismissActionText: "Close",
        dismissAction: () => {
          trackEvent(
            appInsights,
            appInsightsEvents.Usage.Actions.Payment.PaymentFailedDialog,
            context,
            {
              cafeId: currentCart?.cafeId,
              cartId: currentCart?.id,
              cafeName: currentCart?.cafeName,
              component: "Checkout",
            },
          );
          setOrderError(undefined);
          setDialogProps(undefined);
        },
      });
  }, [setDialogProps, orderError, appInsights, context, currentCart, setOrderError]);

  useEffect(() => {
    if (applePayEnabled) {
      setInitialPaymentType("ApplePay");
    } else {
      setInitialPaymentType("Card");
    }
  }, [applePayEnabled]);

  const onSubmit = useCallback(
    (data: CardFormValues) => {
      if (data.paymentType === "ApplePay") {
        payWithApplePay();
      } else {
        submit(data);
      }
    },
    [submit, payWithApplePay],
  );

  const formik = useFormik<CardFormValues>({
    initialValues: {
      paymentType: initialPaymentType,
      cardholderName: "",
      cardNumber: "",
      expiryDate: "",
      cvc: "",
    },
    validateOnMount: true,
    enableReinitialize: true,
    validate: (values) => {
      const errors: Partial<CardFormValues> = {};
      if (values.paymentType === "ApplePay") {
        return errors;
      }
      if (values.cardholderName === "") {
        errors.cardholderName = "Enter a cardholder name";
      }
      if (values.cardNumber === "") {
        errors.cardNumber = "Enter a card number";
      }
      if (values.expiryDate === "") {
        errors.expiryDate = "Enter an expiration date";
      }
      if (values.cvc === "") {
        errors.cvc = "Enter a CVC";
      }
      return errors;
    },
    onSubmit,
  });

  const onAddItem = useCallback(() => {
    // TODO: figure out what to do if there are items in cart from multiple stations
    const stationId = currentCart?.items?.find((item) => item.stationId)?.stationId;
    trackEvent(appInsights, appInsightsEvents.Usage.Actions.AddItemToCart, context, {
      cafeId: currentCart?.cafeId,
      cartId: currentCart?.id,
      cafeName: currentCart?.cafeName,
      component: "Checkout",
      stationId,
    });
    if (stationId) {
      navigate(`/cafe/${cafeId}/station/${stationId}`);
    } else {
      navigate(`/cafe/${cafeId}`);
    }
  }, [navigate, cafeId, currentCart]);

  const onEditItem = useCallback(
    (cafeId: string, itemId: string, id: string) => {
      const itemToEdit = currentCart?.items?.find((item) => item.itemId === itemId)?.itemId;
      const editItemId = currentCart?.items?.find((item) => item.id === id)?.id;
      if (itemToEdit) {
        const stationId = currentCart.items.find((item) => item.itemId === itemId)?.stationId;
        trackEvent(appInsights, appInsightsEvents.Usage.Actions.ItemEditInCart, context, {
          cafeId: currentCart?.cafeId,
          cartId: currentCart?.id,
          cafeName: currentCart?.cafeName,
          component: "Checkout",
          stationId,
        });
        navigate(`/cafe/${cafeId}/station/${stationId}/item/${itemToEdit}/edit/${editItemId}`);
      } else {
        navigate(`/cafe/${cafeId}`);
      }
    },

    [navigate, cafeId, currentCart],
  );

  const onRemoveItem = useCallback(
    (cafeId: string, itemId: string) => {
      setDialogProps({
        content: "Are you sure you want to remove this item from the cart?",
        open: true,
        title: "Remove Item",
        confirmAction: () => {
          trackEvent(
            appInsights,
            appInsightsEvents.Usage.Actions.RemoveItemDialogConfirmed,
            context,
            {
              cafeId: currentCart?.cafeId,
              cartId: currentCart?.id,
              cafeName: currentCart?.cafeName,
              component: "Checkout",
              itemId,
            },
          );
          deleteItemFromCart(cafeId, itemId);
          setDialogProps(undefined);
        },
        dismissAction: () => {
          trackEvent(
            appInsights,
            appInsightsEvents.Usage.Actions.RemoveItemDialogDismissed,
            context,
            {
              cafeId: currentCart?.cafeId,
              cartId: currentCart?.id,
              cafeName: currentCart?.cafeName,
              component: "Checkout",
              itemId,
            },
          );
          setDialogProps(undefined);
        },
      });
    },
    [setDialogProps, deleteItemFromCart, appInsights, context, currentCart],
  );

  const onCancel = useCallback(() => {
    setDialogProps({
      content: "Are you sure you want to abandon the cart for this order?",
      open: true,
      title: "Abandon Order",
      confirmAction: () => {
        if (currentCart) {
          const { cafeId, id: cartId, cafeName } = currentCart;
          trackEvent(
            appInsights,
            appInsightsEvents.Usage.Actions.AbandonCartDialogConfirmed,
            context,
            {
              cafeId,
              cartId,
              cafeName,
              component: "Checkout",
            },
          );
          abandonCart();
          formik.resetForm();
          setDialogProps(undefined);
          navigate(`/cafe/${cafeId}`, { replace: true });
        }
      },
      dismissAction: () => {
        trackEvent(
          appInsights,
          appInsightsEvents.Usage.Actions.AbandonCartDialogDismissed,
          context,
          {
            cafeId: currentCart?.cafeId,
            cartId: currentCart?.id,
            cafeName: currentCart?.cafeName,
            component: "Checkout",
          },
        );
        setDialogProps(undefined);
      },
    });
  }, [setDialogProps, abandonCart, navigate, appInsights, context, currentCart, formik]);

  const onSelectPaymentType = useCallback(
    (paymentType: PaymentType, data: SwitchOnChangeData) => {
      trackEvent(appInsights, appInsightsEvents.Usage.Actions.SelectPaymentOption, context, {
        componentName: "Checkout",
        paymentType,
      });
      if (!data.checked) {
        // Don't allow unchecking the payment type.
        if (paymentType === "ApplePay") {
          formik.setFieldValue("paymentType", "Card");
        } else if (paymentType === "Card" && applePayEnabled) {
          formik.setFieldValue("paymentType", "ApplePay");
        }
      } else {
        formik.setFieldValue("paymentType", paymentType);
      }
      formik.setFieldValue("cardholderName", "");
      formik.setFieldValue("cardNumber", "");
      formik.setFieldValue("expiryDate", "");
      formik.setFieldValue("cvc", "");
    },
    [appInsights, applePayEnabled, context, formik],
  );
  // Pass a callback to CartEntries to capture the ref
  const handleRemoveItem = useCallback(
    (cafeId: string, itemId: string, buttonRef?: React.RefObject<HTMLButtonElement>) => {
      lastRemoveButtonRef.current = buttonRef || null;
      setDialogProps({
        content: "Are you sure you want to remove this item from the cart?",
        open: true,
        title: "Remove Item",
        confirmAction: () => {
          trackEvent(
            appInsights,
            appInsightsEvents.Usage.Actions.RemoveItemDialogConfirmed,
            context,
            {
              cafeId: currentCart?.cafeId,
              cartId: currentCart?.id,
              cafeName: currentCart?.cafeName,
              component: "Checkout",
              itemId,
            },
          );
          deleteItemFromCart(cafeId, itemId);
          setDialogProps(undefined);
          // Restore focus after dialog closes
          setTimeout(() => {
            lastRemoveButtonRef.current?.current?.focus();
          }, 0);
          console.log("lastRemoveButtonRef", lastRemoveButtonRef.current?.current);
          console.log("Focus restored to remove button on confirm:", document.activeElement);
        },
        dismissAction: () => {
          trackEvent(
            appInsights,
            appInsightsEvents.Usage.Actions.RemoveItemDialogDismissed,
            context,
            {
              cafeId: currentCart?.cafeId,
              cartId: currentCart?.id,
              cafeName: currentCart?.cafeName,
              component: "Checkout",
              itemId,
            },
          );
          setDialogProps(undefined);
          // Restore focus after dialog closes
          setTimeout(() => {
            lastRemoveButtonRef.current?.current?.focus();
          }, 0);
          console.log("lastRemoveButtonRef dismiss", lastRemoveButtonRef.current?.current);
          console.log("Focus restored to remove button on confirm:", document.activeElement);
        },
      });
    },
    [setDialogProps, deleteItemFromCart, appInsights, context, currentCart],
  );
  return loadingOrder || loadingCart ? (
    <div className={styles.container}>
      {" "}
      <LoadingScreen />
    </div>
  ) : (
    <FormikProvider value={formik}>
      <Form className={styles.container} data-testid="Checkout">
        <main className={styles.main}>
          <div className={styles.subMainContainer}>
            <div className={styles.cartEntries}>
              <CartEntries
                cart={currentCart}
                onAddItem={onAddItem}
                onRemoveItem={(cafeId, itemId, buttonRef) => {
                  handleRemoveItem(cafeId, itemId, buttonRef);
                }}
                onEditItem={onEditItem}
              />
              <footer className={styles.orderButtons}>
                <Button
                  appearance="primary"
                  disabled={
                    !currentCart ||
                    currentCart.items.length === 0 ||
                    (formik.values.paymentType !== "ApplePay" && !formik.isValid)
                  }
                  className={mergeClasses(
                    styles.semiBoldBaseFont,
                    styles.paddingAndBorderRadius,
                    styles.orderButton,
                  )}
                  shape="rounded"
                  type="submit"
                  data-testid="orderBtn"
                  tabIndex={8} // Updated tabIndex
                >
                  Order now
                </Button>
                <Button
                  className={mergeClasses(
                    styles.cancelButton,
                    styles.semiBoldBaseFont,
                    styles.paddingAndBorderRadius,
                  )}
                  appearance="outline"
                  disabled={!currentCart || currentCart.items.length === 0}
                  onClick={onCancel}
                  shape="rounded"
                  tabIndex={9} // Updated tabIndex
                >
                  Cancel
                </Button>
              </footer>
            </div>
            <div className={styles.payments}>
              <CartHeader cart={currentCart} />
              <Payments
                disabled={!currentCart || currentCart.items.length === 0}
                selected={formik.values.paymentType}
                onSelect={onSelectPaymentType}
              />
            </div>
          </div>
        </main>
        <div className={styles.mobileViewContainer}>
          <CartHeader cart={currentCart} />
          <CartEntries
            cart={currentCart}
            onAddItem={onAddItem}
            onRemoveItem={onRemoveItem}
            onEditItem={onEditItem}
          />
          <Payments
            disabled={!currentCart || currentCart.items.length === 0}
            selected={formik.values.paymentType}
            onSelect={onSelectPaymentType}
          />

          {/* If we want the "Order Now" buttons to consistently appear underneath the selected payment option's form, we need to refactor this so the form and footer buttons are inside PaymentOptions. */}
          <footer className={styles.orderButtons}>
            <Button
              appearance="primary"
              disabled={
                !currentCart ||
                currentCart.items.length === 0 ||
                (formik.values.paymentType !== "ApplePay" && !formik.isValid)
              }
              className={mergeClasses(styles.semiBoldBaseFont, styles.paddingAndBorderRadius)}
              shape="rounded"
              type="submit"
              data-testid="orderBtn"
              tabIndex={8} // Updated tabIndex
            >
              Order now
            </Button>
            <Button
              className={mergeClasses(
                styles.cancelButton,
                styles.semiBoldBaseFont,
                styles.paddingAndBorderRadius,
              )}
              appearance="outline"
              disabled={!currentCart || currentCart.items.length === 0}
              onClick={onCancel}
              shape="rounded"
              tabIndex={9} // Updated tabIndex
            >
              Cancel
            </Button>
          </footer>
        </div>
        {dialogProps && <ConfirmationDialog {...dialogProps} />}
      </Form>
    </FormikProvider>
  );
};
